class SignupSliderUtils {
  // SMD-895: the published unit-based rates ARE the annual (paid up front) rates;
  // month-to-month is 10% above them. Rates were previously stored one period off,
  // which is why annual pricing was quoted 10% below the real one.
  //
  // Each bracket's rate applies only to the units inside that bracket (graduated,
  // Stripe-style); the last bracket's rate extends past its published bound.
  //
  // Both rate tables are spelled out rather than derived from the 10% spread:
  // the published month-to-month rates are rounded to the cent, so a few of them
  // (0.83, 0.50, 2.04, 1.71) are not exactly the annual rate x 1.1.
  static ANNUAL_UNIT_RATES = {
    pro: [
      { upTo: 250, rate: 1.30 },
      { upTo: 500, rate: 0.80 },
      { upTo: 3000, rate: 0.75 },
      { upTo: Infinity, rate: 0.45 }
    ],
    ultra: [
      { upTo: 250, rate: 2.70 },
      { upTo: 500, rate: 2.10 },
      { upTo: 3000, rate: 1.85 },
      { upTo: Infinity, rate: 1.55 }
    ]
  };

  static MONTH_TO_MONTH_UNIT_RATES = {
    pro: [
      { upTo: 250, rate: 1.43 },
      { upTo: 500, rate: 0.88 },
      { upTo: 3000, rate: 0.83 },
      { upTo: Infinity, rate: 0.50 }
    ],
    ultra: [
      { upTo: 250, rate: 2.97 },
      { upTo: 500, rate: 2.31 },
      { upTo: 3000, rate: 2.04 },
      { upTo: Infinity, rate: 1.71 }
    ]
  };

  // SMD-895: an annual plan cannot be paid monthly, so the only unit-based
  // periods offered are month-to-month and annual (paid up front).
  static UNIT_PRICING_RULES = {
    month_to_month: SignupSliderUtils.MONTH_TO_MONTH_UNIT_RATES,
    yearly: SignupSliderUtils.ANNUAL_UNIT_RATES
  };

  static MIN_UNITS = 50;

  static formatUSD(value) {
    return '$' + (
      Number.isInteger(value)
        ? value.toString()
        : value.toFixed(2).replace(/\.00$/, '')
    );
  }

  // The published tiers only quote whole steps (2 / 10 / 30 / 100 / 200 listings),
  // so a count in between is priced as the cheapest tier that covers it: its base
  // rate plus its own per-listing rate for everything above what it includes.
  static calculateListingPrice(planKey, units, listingsBasedPricingRules) {
    const tiers = listingsBasedPricingRules[planKey]?.tiers;
    // No published tier for this family (e.g. the Live Answer plans are all
    // unpublished): the caller hides the card instead of pricing it.
    if (!tiers?.length) return null;

    const listings = Number(units) || 0;

    const results = tiers.map(tier => {
      const extraUnits = Math.max(0, listings - tier.included);
      const addonsMonthly = extraUnits * tier.addon;
      const totalMonthly = tier.basePrice + addonsMonthly;

      return {
        plan_id: tier.plan_id,
        included: tier.included,
        baseMonthly: tier.basePrice,
        addonsMonthly,
        totalMonthly,
        extraUnits
      };
    });

    return results.reduce((best, current) =>
      current.totalMonthly < best.totalMonthly ? current : best
    );
  }

  static buildListingPricingRules(plans) {
    const result = {
      pro: { tiers: [] },
      live: { tiers: [] }
    };

    plans.forEach(plan => {
      const {
        id,
        base_rate,
        listings_included,
        listings_rate,
        live_answer_bundle_policy
      } = plan;

      const groupKey = live_answer_bundle_policy === 'NONE'  ? 'pro' : 'live';

      result[groupKey].tiers.push({
        plan_id: id,
        basePrice: +base_rate,
        included: listings_included,
        // SMD-895: listing-based plans price extra listings through listings_rate.
        // overage_rate belongs to lead-based plans and is 0 on every listing plan,
        // so reading it made every extra listing free - and the cheapest tier below
        // always won, quoting the smallest plan's base rate for any listing count.
        addon: +listings_rate
      });
    });

    result.pro.tiers.sort((a, b) => a.included - b.included);
    result.live.tiers.sort((a, b) => a.included - b.included);

    return result;
  }

  static calculateUnitPricing({ units, plan, billingPeriod }) {
    const tiers = SignupSliderUtils.UNIT_PRICING_RULES[billingPeriod][plan];

    // SMD-895: unit-based plans start at 50 units, so anything below that is
    // priced as 50 - including the per-unit rate, which used to divide the
    // 50-unit total by the smaller entered number.
    const billedUnits = Math.max(Number(units) || 0, SignupSliderUtils.MIN_UNITS);

    let remaining = billedUnits;
    let total = 0;
    let prevLimit = 0;

    for (const tier of tiers) {
      const tierUnits = Math.min(
        remaining,
        tier.upTo - prevLimit
      );

      if (tierUnits <= 0) break;

      total += tierUnits * tier.rate;
      remaining -= tierUnits;
      prevLimit = tier.upTo;
    }

    return {
      monthlyTotal: total,
      perUnit: total / billedUnits
    };
  }
}

export default SignupSliderUtils;
